import { Messages } from '../../../../../../gen-src/Localize';
import { assertUnreachable } from '../../utils/TypescriptHelpers';

/**
 * @internal
 */
export const labelServiceAssetType = ['datasource', 'database', 'table', 'virtualConnection', 'virtualConnectionTable', 'flow', 'column'] as const;

/**
 * A @type {LabelServiceAssetType} is the REST API representation of assets that may have labels associated with them directly.
 */
export type LabelServiceAssetType = typeof labelServiceAssetType[number];

export function isLabelServiceAssetType(value: string): value is LabelServiceAssetType {
  return labelServiceAssetType.find(t => t === value) !== undefined;
}

/**
 * @internal
 */
export const labelAssetType = ['CloudFile', 'DatabaseServer', 'DatabaseTable', 'File', 'Flow', 'PublishedDatasource', 'WebDataConnector', 'VirtualConnectionTable', 'VirtualConnection', 'Column'] as const;

/**
 * A @type {LabelAssetType} is the GraphQL representation of assets that may have labels associated with them either directly or by inheritance.
 */
export type LabelAssetType = typeof labelAssetType[number];

export function isLabelAssetType(value: string): value is LabelAssetType {
  return labelAssetType.find(t => t === value) !== undefined;
}

/**
 * @internal
 */
export const labelSubjectArea = ['View', 'Metric', 'metric', 'Workbook', 'workbook', 'EmbeddedDatasource', 'Field', 'FlowColumnOutputField', 'FlowFieldOutputField'] as const;

/**
 * A @type {LabelSubjectArea} is anywhere a label chip can be rendered, e.g.: on a workbook I want to know what upstream labels there are.
 */
export type LabelSubjectArea =
  | typeof labelSubjectArea[number]
  | LabelServiceAssetType
  | LabelAssetType;

export function catalogLabelAssetTypeToLabelAssetType(type: LabelAssetType | undefined | null): LabelServiceAssetType | undefined | null {
  switch (type) {
    case 'WebDataConnector': return 'database';
    case 'DatabaseServer': return 'database';
    case 'CloudFile': return 'database';
    case 'File': return 'database';
    case 'DatabaseTable': return 'table';
    case 'Flow': return 'flow';
    case 'PublishedDatasource': return 'datasource';
    case 'VirtualConnection': return 'virtualConnection';
    case 'VirtualConnectionTable': return 'virtualConnectionTable';
    case 'Column': return 'column';
    case null: return null;
    case undefined: return undefined;
    default:
      assertUnreachable(type);
      return null;
  }
}


export function onThisAssetMessage(contentType: LabelAssetType | LabelServiceAssetType | null): string {
  if (!contentType) {
    return Messages.Label_direct_asset();
  }

  switch (contentType) {
    case 'column': case 'Column':
      return Messages.Label_direct_column();
    case 'database': case 'CloudFile': case 'DatabaseServer': case 'File': case 'WebDataConnector':
      return Messages.Label_direct_database()
    case 'table': case 'DatabaseTable': case 'VirtualConnectionTable': case 'virtualConnectionTable':
      return Messages.Label_direct_table();
    case 'flow': case 'Flow':
      return Messages.Label_direct_flow();
    case 'datasource': case 'PublishedDatasource':
      return Messages.Label_direct_datasource();
    case 'virtualConnection': case 'VirtualConnection':
      return Messages.Label_direct_connection();
    default:
      assertUnreachable(contentType);
      return '';
  }
}

export function inheritedByThisAssetMessage(contentType: LabelSubjectArea | null, count?: number): string {
  if (!contentType) {
    return Messages.Label_inherited_asset();
  }

  switch (contentType) {
    case 'column': case 'Column':
      return Messages.Label_inherited_column({
        MESSAGE_COUNT: count ?? 0,

      });
    case 'database': case 'CloudFile': case 'DatabaseServer': case 'File': case 'WebDataConnector':
      return Messages.Label_inherited_database()
    case 'table': case 'DatabaseTable': case 'VirtualConnectionTable': case 'virtualConnectionTable':
      return Messages.Label_inherited_table();
    case 'flow': case 'Flow':
      return Messages.Label_inherited_flow();
    case 'datasource': case 'PublishedDatasource': case 'EmbeddedDatasource':
      return Messages.Label_inherited_datasource();
    case 'virtualConnection': case 'VirtualConnection':
      return Messages.Label_inherited_connection();
    case 'View':
      return Messages.Label_inherited_view();
    case 'Workbook': case 'workbook':
      return Messages.Label_inherited_workbook();
    case 'Field':
    case 'FlowColumnOutputField':
    case 'FlowFieldOutputField':
      return Messages.Label_inherited_field({
        MESSAGE_COUNT: count ?? 0,
      });
    case 'Metric': case 'metric':
      return Messages.Label_inherited_metric();
    default:
      assertUnreachable(contentType);
      return '';
  }
}
